import { useState, useEffect, useRef, useCallback } from 'react';
import { motion } from 'framer-motion';
import { 
  BarChart3, 
  Download,
  Share2,
  Calendar,
  TrendingUp,
  Target,
  FileText,
  Link2,
  Globe,
  ArrowUpRight,
  ArrowDownRight,
  CheckCircle2,
  AlertCircle,
  Search,
  MousePointerClick,
  Eye,
  Copy,
  Check,
  Loader2,
  X,
  ScanSearch,
  Clock,
  Settings,
  History,
} from 'lucide-react';
import SEOAuditReport from './reports/SEOAuditReport';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '../../components/ui/dialog';
import { api } from '../../context/AuthContext';
import { toast } from 'sonner';
import { useLocation } from 'react-router-dom';
import { 
  AreaChart, 
  Area, 
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip, 
  ResponsiveContainer,
} from 'recharts';

export default function ReportsPage() {
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [days, setDays] = useState(30);
  const [pdfLoading, setPdfLoading] = useState(false);
  const [shareDialogOpen, setShareDialogOpen] = useState(false);
  const [shareLink, setShareLink] = useState('');
  const [shareLoading, setShareLoading] = useState(false);
  const [copied, setCopied] = useState(false);
  const [activeTab, setActiveTab] = useState('performance');
  const [seoReport, setSeoReport] = useState(null);
  const [seoReportLoading, setSeoReportLoading] = useState(false);
  const [dailyReport, setDailyReport] = useState(null);
  const [weeklyReport, setWeeklyReport] = useState(null);
  const [dailyLoading, setDailyLoading] = useState(false);
  const [weeklyLoading, setWeeklyLoading] = useState(false);
  const [schedule, setSchedule] = useState(null);
  const [scheduleOpen, setScheduleOpen] = useState(false);
  const [reportHistory, setReportHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const reportRef = useRef(null);
  const location = useLocation();

  useEffect(() => {
    if (location.state?.tab === 'seo-audit') setActiveTab('seo-audit');
  }, [location.state]);

  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        fetchReport();
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [days]);

  useEffect(() => {
    fetchReport();
  }, [days]);

  useEffect(() => {
    if (activeTab === 'performance') {
      fetchReport();
    }
  }, [activeTab]);

  const fetchReport = async () => {
    setLoading(true);
    try {
      const response = await api.get(`/reports/demo_project/monthly?days=${days}`);
      setReport(response.data);
    } catch (error) {
      console.error('Error fetching report:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchSeoReport = useCallback(async () => {
    setSeoReportLoading(true);
    try {
      const scansRes = await api.get('/onpage/scans');
      const scans = scansRes.data;
      if (scans.length > 0) {
        const latest = scans[0];
        const reportRes = await api.get(`/onpage/scans/${latest.scan_id}/report`);
        setSeoReport(reportRes.data);
      } else {
        setSeoReport(null);
      }
    } catch (error) {
      console.error('Error fetching SEO report:', error);
      setSeoReport(null);
    } finally {
      setSeoReportLoading(false);
    }
  }, []);

  useEffect(() => {
    if (activeTab === 'seo-audit') {
      fetchSeoReport();
    }
    if (activeTab === 'daily') {
      fetchDailyReport();
    }
    if (activeTab === 'weekly') {
      fetchWeeklyReport();
    }
    if (activeTab === 'history') {
      fetchReportHistory();
    }
  }, [activeTab]);

  const fetchDailyReport = async () => {
    setDailyLoading(true);
    try {
      const res = await api.get('/reports/demo_project/daily');
      setDailyReport(res.data);
    } catch (err) {
      console.error('Daily report error:', err);
    } finally {
      setDailyLoading(false);
    }
  };

  const fetchWeeklyReport = async () => {
    setWeeklyLoading(true);
    try {
      const res = await api.get('/reports/demo_project/weekly');
      setWeeklyReport(res.data);
    } catch (err) {
      console.error('Weekly report error:', err);
    } finally {
      setWeeklyLoading(false);
    }
  };

  const fetchSchedule = async () => {
    try {
      const res = await api.get('/reports/demo_project/schedule');
      setSchedule(res.data);
    } catch (err) {
      console.error('Schedule fetch error:', err);
    }
  };

  const fetchReportHistory = async () => {
    setHistoryLoading(true);
    try {
      const res = await api.get('/reports/demo_project/history?limit=20');
      setReportHistory(res.data);
    } catch (err) {
      console.error('History fetch error:', err);
    } finally {
      setHistoryLoading(false);
    }
  };

  const updateSchedule = async (updates) => {
    try {
      await api.post('/reports/demo_project/schedule', updates);
      toast.success('Schedule updated');
      setSchedule(prev => ({ ...prev, ...updates }));
    } catch (err) {
      toast.error('Failed to update schedule');
    }
  };

  const handleExportPDF = useCallback(async () => {
    if (!reportRef.current) return;
    setPdfLoading(true);
    try {
      const html2canvas = (await import('html2canvas')).default;
      const { jsPDF } = await import('jspdf');

      const element = reportRef.current;
      const canvas = await html2canvas(element, {
        scale: 2,
        useCORS: true,
        logging: false,
        backgroundColor: '#ffffff',
        windowWidth: 1200,
      });

      const imgData = canvas.toDataURL('image/png');
      const imgWidth = 210; // A4 width in mm
      const pageHeight = 297; // A4 height in mm
      const imgHeight = (canvas.height * imgWidth) / canvas.width;

      const pdf = new jsPDF('p', 'mm', 'a4');
      let heightLeft = imgHeight;
      let position = 0;

      pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
      heightLeft -= pageHeight;

      while (heightLeft > 0) {
        position = heightLeft - imgHeight;
        pdf.addPage();
        pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
        heightLeft -= pageHeight;
      }

      const filename = `GDS_Report_${report?.period || 'Monthly'}_${days}d.pdf`;
      pdf.save(filename);
      toast.success('PDF exported successfully');
    } catch (err) {
      console.error('PDF export error:', err);
      toast.error('Failed to export PDF. Try again.');
    } finally {
      setPdfLoading(false);
    }
  }, [report, days]);

  const handleShare = async () => {
    setShareDialogOpen(true);
    setShareLoading(true);
    setShareLink('');
    setCopied(false);
    try {
      const res = await api.post('/reports/demo_project/share', { days });
      const token = res.data.share_token;
      const link = `${window.location.origin}/shared/report/${token}`;
      setShareLink(link);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to generate share link');
      setShareDialogOpen(false);
    } finally {
      setShareLoading(false);
    }
  };

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(shareLink);
      setCopied(true);
      toast.success('Link copied to clipboard');
      setTimeout(() => setCopied(false), 3000);
    } catch {
      const input = document.createElement('input');
      input.value = shareLink;
      document.body.appendChild(input);
      input.select();
      document.execCommand('copy');
      document.body.removeChild(input);
      setCopied(true);
      toast.success('Link copied to clipboard');
      setTimeout(() => setCopied(false), 3000);
    }
  };

  const stats = report?.executive_summary || {};
  const hasRealData = report?.has_real_data;
  const hasGA4 = report?.data_sources?.ga4;
  const hasGSC = report?.data_sources?.gsc;
  const keywordRankings = report?.keyword_rankings;
  const executionActivity = report?.execution_activity;
  const onpageImprovements = report?.onpage_improvements;

  const formatChange = (value) => {
    if (!value && value !== 0) return null;
    const isPositive = value > 0;
    return (
      <div className={`flex items-center gap-1 mt-1 ${isPositive ? 'text-emerald-300' : 'text-red-300'}`}>
        {isPositive ? <ArrowUpRight className="w-4 h-4" /> : <ArrowDownRight className="w-4 h-4" />}
        <span className="text-sm">{isPositive ? '+' : ''}{value}%</span>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-indigo-500"></div>
      </div>
    );
  }

  return (
    <div className="space-y-8" data-testid="reports-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="font-heading text-2xl font-bold mb-1">Reports</h1>
          <p className="text-slate-600 dark:text-slate-400">
            Detailed analytics and SEO audit reports
          </p>
        </div>
        <div className="flex items-center gap-3">
          {activeTab === 'performance' && (
            <>
              <div className="flex gap-1">
                {[7, 14, 30, 60, 90].map((d) => (
                  <Button
                    key={d}
                    variant={days === d ? 'default' : 'ghost'}
                    size="sm"
                    className="h-8 px-2 text-xs"
                    onClick={() => setDays(d)}
                  >
                    {d}d
                  </Button>
                ))}
              </div>
              <Button variant="outline" onClick={handleShare} data-testid="share-report-btn">
                <Share2 className="w-4 h-4 mr-2" />
                Share
              </Button>
              <Button
                className="gds-btn-primary"
                onClick={handleExportPDF}
                disabled={pdfLoading}
                data-testid="download-report-btn"
              >
                {pdfLoading ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <Download className="w-4 h-4 mr-2" />
                )}
                {pdfLoading ? 'Generating...' : 'Export PDF'}
              </Button>
            </>
          )}
        </div>
      </div>

      {/* Tab Navigation */}
      <div className="flex gap-1 p-1 bg-slate-100 dark:bg-slate-800 rounded-xl w-fit flex-wrap">
        <button
          onClick={() => setActiveTab('daily')}
          className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all ${
            activeTab === 'daily'
              ? 'bg-white dark:bg-slate-700 text-indigo-600 dark:text-indigo-400 shadow-sm'
              : 'text-slate-500 hover:text-slate-700'
          }`}
        >
          <Clock className="w-4 h-4" />
          Daily
        </button>
        <button
          onClick={() => setActiveTab('weekly')}
          className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all ${
            activeTab === 'weekly'
              ? 'bg-white dark:bg-slate-700 text-indigo-600 dark:text-indigo-400 shadow-sm'
              : 'text-slate-500 hover:text-slate-700'
          }`}
        >
          <Calendar className="w-4 h-4" />
          Weekly
        </button>
        <button
          onClick={() => setActiveTab('performance')}
          className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all ${
            activeTab === 'performance'
              ? 'bg-white dark:bg-slate-700 text-indigo-600 dark:text-indigo-400 shadow-sm'
              : 'text-slate-500 hover:text-slate-700'
          }`}
        >
          <BarChart3 className="w-4 h-4" />
          Monthly
        </button>
        <button
          onClick={() => setActiveTab('seo-audit')}
          className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all ${
            activeTab === 'seo-audit'
              ? 'bg-white dark:bg-slate-700 text-indigo-600 dark:text-indigo-400 shadow-sm'
              : 'text-slate-500 hover:text-slate-700'
          }`}
        >
          <ScanSearch className="w-4 h-4" />
          SEO Audit
        </button>
        <button
          onClick={() => { setActiveTab('history'); fetchReportHistory(); }}
          className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all ${
            activeTab === 'history'
              ? 'bg-white dark:bg-slate-700 text-indigo-600 dark:text-indigo-400 shadow-sm'
              : 'text-slate-500 hover:text-slate-700'
          }`}
        >
          <History className="w-4 h-4" />
          History
        </button>
        <button
          onClick={() => { setScheduleOpen(true); fetchSchedule(); }}
          className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium text-slate-500 hover:text-slate-700 transition-all"
        >
          <Settings className="w-4 h-4" />
          Schedule
        </button>
      </div>

      {/* Tab Content */}
      {activeTab === 'daily' ? (
        <DailyReportSection report={dailyReport} loading={dailyLoading} onRefresh={fetchDailyReport} />
      ) : activeTab === 'weekly' ? (
        <WeeklyReportSection report={weeklyReport} loading={weeklyLoading} onRefresh={fetchWeeklyReport} />
      ) : activeTab === 'history' ? (
        <ReportHistorySection history={reportHistory} loading={historyLoading} />
      ) : activeTab === 'seo-audit' ? (
        <SEOAuditReport report={seoReport} loading={seoReportLoading} />
      ) : (
      <>

      {/* Report content (captured for PDF) */}
      <div ref={reportRef} className="space-y-8">
        {/* No data banner */}
        {!hasRealData && (
          <Card className="border-amber-200 bg-amber-50 dark:bg-amber-900/20">
            <CardContent className="p-4 flex items-center gap-4">
              <AlertCircle className="w-5 h-5 text-amber-600" />
              <p className="text-sm text-amber-700">
                Connect Google Analytics and Search Console in Integrations to see real report data.
              </p>
            </CardContent>
          </Card>
        )}

        {/* Executive Summary */}
        <Card className="gds-card bg-gradient-to-br from-indigo-500 to-violet-600 text-white border-0">
          <CardHeader>
            <CardTitle className="font-heading text-white">Executive Summary</CardTitle>
            {hasRealData && (
              <CardDescription className="text-indigo-200">
                {hasGA4 && 'GA4'}{hasGA4 && hasGSC && ' + '}{hasGSC && 'Search Console'} data &middot; Last {days} days
              </CardDescription>
            )}
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-6">
              <div className="p-4 rounded-xl bg-white/10 backdrop-blur-sm">
                <p className="text-indigo-100 text-sm mb-1">Organic Traffic</p>
                <p className="text-2xl font-bold font-heading">{stats.organic_traffic?.toLocaleString() || 0}</p>
                {formatChange(stats.traffic_change)}
              </div>
              <div className="p-4 rounded-xl bg-white/10 backdrop-blur-sm">
                <p className="text-indigo-100 text-sm mb-1">Conversions</p>
                <p className="text-2xl font-bold font-heading">{stats.conversions?.toLocaleString() || 0}</p>
                {formatChange(stats.conversion_change)}
              </div>
              <div className="p-4 rounded-xl bg-white/10 backdrop-blur-sm">
                <p className="text-indigo-100 text-sm mb-1">Search Clicks</p>
                <p className="text-2xl font-bold font-heading">{stats.total_clicks?.toLocaleString() || 0}</p>
              </div>
              <div className="p-4 rounded-xl bg-white/10 backdrop-blur-sm">
                <p className="text-indigo-100 text-sm mb-1">Keywords Tracked</p>
                <p className="text-2xl font-bold font-heading">{stats.keyword_count?.toLocaleString() || 0}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Traffic Growth + Quick Stats */}
        <div className="grid lg:grid-cols-3 gap-6">
          <Card className="lg:col-span-2 gds-card">
            <CardHeader>
              <CardTitle className="font-heading flex items-center gap-2">
                <TrendingUp className="w-5 h-5 text-indigo-500" />
                Traffic Growth
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="h-72">
                {report?.traffic_data?.chart_data?.length > 0 ? (
                  <ResponsiveContainer width="100%" height="100%">
                    <AreaChart data={report.traffic_data.chart_data}>
                      <defs>
                        <linearGradient id="trafficGradient" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor="#6366f1" stopOpacity={0.3}/>
                          <stop offset="95%" stopColor="#6366f1" stopOpacity={0}/>
                        </linearGradient>
                      </defs>
                      <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                      <XAxis dataKey="month" stroke="#94a3b8" fontSize={12} />
                      <YAxis stroke="#94a3b8" fontSize={12} />
                      <Tooltip 
                        contentStyle={{ 
                          backgroundColor: '#fff', 
                          border: '1px solid #e2e8f0',
                          borderRadius: '8px'
                        }}
                      />
                      <Area 
                        type="monotone" 
                        dataKey="traffic" 
                        stroke="#6366f1" 
                        fillOpacity={1} 
                        fill="url(#trafficGradient)"
                        strokeWidth={2}
                        name="Sessions"
                      />
                    </AreaChart>
                  </ResponsiveContainer>
                ) : (
                  <div className="flex flex-col items-center justify-center h-full text-slate-400">
                    <BarChart3 className="w-12 h-12 mb-3" />
                    <p className="text-sm">No traffic data available</p>
                    <p className="text-xs mt-1">Connect GA4 to see traffic trends</p>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>

          <Card className="gds-card">
            <CardHeader>
              <CardTitle className="font-heading flex items-center gap-2">
                <Target className="w-5 h-5 text-cyan-500" />
                Quick Stats
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div className="flex items-center gap-3 p-3 rounded-lg bg-slate-50 dark:bg-slate-800/50">
                  <div className="w-10 h-10 rounded-lg bg-indigo-100 flex items-center justify-center">
                    <Eye className="w-5 h-5 text-indigo-600" />
                  </div>
                  <div>
                    <p className="text-lg font-bold">{stats.total_impressions?.toLocaleString() || 0}</p>
                    <p className="text-xs text-slate-500">Search Impressions</p>
                  </div>
                </div>
                <div className="flex items-center gap-3 p-3 rounded-lg bg-slate-50 dark:bg-slate-800/50">
                  <div className="w-10 h-10 rounded-lg bg-cyan-100 flex items-center justify-center">
                    <MousePointerClick className="w-5 h-5 text-cyan-600" />
                  </div>
                  <div>
                    <p className="text-lg font-bold">{stats.total_clicks?.toLocaleString() || 0}</p>
                    <p className="text-xs text-slate-500">Search Clicks</p>
                  </div>
                </div>
                <div className="flex items-center gap-3 p-3 rounded-lg bg-slate-50 dark:bg-slate-800/50">
                  <div className="w-10 h-10 rounded-lg bg-emerald-100 flex items-center justify-center">
                    <Search className="w-5 h-5 text-emerald-600" />
                  </div>
                  <div>
                    <p className="text-lg font-bold">{stats.keyword_count || 0}</p>
                    <p className="text-xs text-slate-500">Keywords Ranking</p>
                  </div>
                </div>
                <div className="flex items-center gap-3 p-3 rounded-lg bg-slate-50 dark:bg-slate-800/50">
                  <div className="w-10 h-10 rounded-lg bg-violet-100 flex items-center justify-center">
                    <FileText className="w-5 h-5 text-violet-600" />
                  </div>
                  <div>
                    <p className="text-lg font-bold">{stats.content_published || 0}</p>
                    <p className="text-xs text-slate-500">Content Published</p>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Keyword Rankings */}
        {keywordRankings && keywordRankings.keywords?.length > 0 && (
          <Card className="gds-card">
            <CardHeader>
              <CardTitle className="font-heading flex items-center gap-2">
                <Target className="w-5 h-5 text-indigo-500" />
                Keyword Rankings
              </CardTitle>
              <CardDescription>
                {keywordRankings.total_tracked} tracked &middot;{' '}
                <span className="text-emerald-600">{keywordRankings.improved} improved</span> &middot;{' '}
                <span className="text-red-600">{keywordRankings.declined} declined</span>
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-slate-200 dark:border-slate-700">
                      <th className="text-left py-3 px-3 text-xs font-medium text-slate-500 uppercase">Keyword</th>
                      <th className="text-right py-3 px-3 text-xs font-medium text-slate-500 uppercase">Position</th>
                      <th className="text-right py-3 px-3 text-xs font-medium text-slate-500 uppercase">Change</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                    {keywordRankings.keywords.map((kw, i) => (
                      <tr key={i} className="hover:bg-slate-50 dark:hover:bg-slate-800/50">
                        <td className="py-2 px-3 font-medium">{kw.keyword}</td>
                        <td className="py-2 px-3 text-right">
                          <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-semibold ${
                            kw.current_position <= 3 ? 'text-emerald-600 bg-emerald-50' :
                            kw.current_position <= 10 ? 'text-blue-600 bg-blue-50' :
                            kw.current_position <= 20 ? 'text-amber-600 bg-amber-50' :
                            'text-slate-600 bg-slate-50'
                          }`}>
                            #{kw.current_position}
                          </span>
                        </td>
                        <td className="py-2 px-3 text-right">
                          {kw.change !== 0 ? (
                            <span className={`flex items-center justify-end gap-1 text-xs font-medium ${
                              kw.change > 0 ? 'text-emerald-600' : 'text-red-600'
                            }`}>
                              {kw.change > 0 ? <ArrowUpRight className="w-3 h-3" /> : <ArrowDownRight className="w-3 h-3" />}
                              {kw.change > 0 ? '+' : ''}{kw.change}
                            </span>
                          ) : (
                            <span className="text-xs text-slate-400">--</span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Activity & On-Page Summary */}
        <div className="grid lg:grid-cols-2 gap-6">
          {/* Execution Activity */}
          {executionActivity && executionActivity.total_actions > 0 && (
            <Card className="gds-card">
              <CardHeader>
                <CardTitle className="font-heading flex items-center gap-2">
                  <CheckCircle2 className="w-5 h-5 text-emerald-500" />
                  Execution Activity
                </CardTitle>
                <CardDescription>{executionActivity.total_actions} total actions this period</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {Object.entries(executionActivity.actions_by_type).map(([action, count]) => (
                    <div key={action} className="flex items-center justify-between p-3 rounded-lg bg-slate-50 dark:bg-slate-800/50">
                      <span className="text-sm font-medium capitalize">
                        {action.replace(/_/g, ' ')}
                      </span>
                      <Badge variant="secondary">{count}</Badge>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* On-Page Improvements */}
          {onpageImprovements && onpageImprovements.scans > 0 && (
            <Card className="gds-card">
              <CardHeader>
                <CardTitle className="font-heading flex items-center gap-2">
                  <Globe className="w-5 h-5 text-violet-500" />
                  On-Page Improvements
                </CardTitle>
                <CardDescription>{onpageImprovements.scans} scans performed this period</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 gap-4">
                  <div className="p-4 rounded-xl bg-slate-50 dark:bg-slate-800/50 text-center">
                    <p className="text-2xl font-bold text-indigo-600">{onpageImprovements.total_recommendations}</p>
                    <p className="text-xs text-slate-500 mt-1">Recommendations</p>
                  </div>
                  <div className="p-4 rounded-xl bg-slate-50 dark:bg-slate-800/50 text-center">
                    <p className="text-2xl font-bold text-emerald-600">{onpageImprovements.approved}</p>
                    <p className="text-xs text-slate-500 mt-1">Approved</p>
                  </div>
                  <div className="p-4 rounded-xl bg-slate-50 dark:bg-slate-800/50 text-center">
                    <p className="text-2xl font-bold text-blue-600">{onpageImprovements.implemented}</p>
                    <p className="text-xs text-slate-500 mt-1">Implemented</p>
                  </div>
                  <div className="p-4 rounded-xl bg-slate-50 dark:bg-slate-800/50 text-center">
                    <p className="text-2xl font-bold text-red-600">{onpageImprovements.rejected}</p>
                    <p className="text-xs text-slate-500 mt-1">Rejected</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}
        </div>

        {/* Top Content */}
        <Card className="gds-card">
          <CardHeader>
            <CardTitle className="font-heading flex items-center gap-2">
              <FileText className="w-5 h-5 text-violet-500" />
              Top Content
            </CardTitle>
            {hasGA4 && <CardDescription>By page views from Google Analytics</CardDescription>}
          </CardHeader>
          <CardContent>
            {report?.content_data?.top_content?.length > 0 ? (
              <div className="space-y-3">
                {report.content_data.top_content.map((content, i) => (
                  <div key={i} className="flex items-center justify-between p-3 rounded-lg bg-slate-50 dark:bg-slate-800/50">
                    <div className="flex-1 min-w-0 mr-4">
                      <p className="font-medium text-sm truncate">{content.title}</p>
                      <p className="text-xs text-slate-500 mt-1">{content.views?.toLocaleString()} views</p>
                    </div>
                    {content.conversions > 0 && (
                      <Badge className="bg-indigo-100 text-indigo-700">
                        {content.conversions} conversions
                      </Badge>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8 text-slate-400">
                <FileText className="w-10 h-10 mx-auto mb-2" />
                <p className="text-sm">No content data available</p>
                <p className="text-xs mt-1">Connect GA4 to see top performing pages</p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
      </>
      )}

      {/* Share Dialog */}
      <Dialog open={shareDialogOpen} onOpenChange={setShareDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Share Report</DialogTitle>
            <DialogDescription>
              Anyone with this link can view a read-only version of this report. The link expires in 30 days.
            </DialogDescription>
          </DialogHeader>
          {shareLoading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="w-6 h-6 animate-spin text-indigo-500" />
              <span className="ml-2 text-sm text-slate-500">Generating link...</span>
            </div>
          ) : shareLink ? (
            <div className="space-y-4 pt-2">
              <div className="flex gap-2">
                <Input
                  readOnly
                  value={shareLink}
                  className="text-sm"
                  onClick={(e) => e.target.select()}
                />
                <Button
                  variant="outline"
                  className="flex-shrink-0"
                  onClick={handleCopyLink}
                >
                  {copied ? (
                    <Check className="w-4 h-4 text-emerald-500" />
                  ) : (
                    <Copy className="w-4 h-4" />
                  )}
                </Button>
              </div>
              <p className="text-xs text-slate-500">
                This link provides a live snapshot of your report data. It will update as new data comes in.
              </p>
            </div>
          ) : null}
        </DialogContent>
      </Dialog>

      {/* Schedule Dialog */}
      <Dialog open={scheduleOpen} onOpenChange={setScheduleOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Settings className="w-5 h-5 text-indigo-500" /> Report Schedule</DialogTitle>
            <DialogDescription>Configure automated report delivery.</DialogDescription>
          </DialogHeader>
          {schedule && (
            <div className="space-y-4 pt-2">
              <ScheduleToggle label="Daily Reports" description="Rank changes and traffic snapshot at 7 AM UTC"
                checked={schedule.daily_enabled} onChange={v => updateSchedule({ daily_enabled: v })} />
              <ScheduleToggle label="Weekly Reports" description="Keyword movement + content performance (Monday 8 AM UTC)"
                checked={schedule.weekly_enabled} onChange={v => updateSchedule({ weekly_enabled: v })} />
              <ScheduleToggle label="Monthly Reports" description="Full executive report"
                checked={schedule.monthly_enabled} onChange={v => updateSchedule({ monthly_enabled: v })} />
              <div>
                <label className="text-sm font-medium text-slate-700 dark:text-slate-300">Email Recipients</label>
                <Input
                  placeholder="email1@example.com, email2@example.com"
                  defaultValue={(schedule.email_recipients || []).join(', ')}
                  onBlur={e => {
                    const emails = e.target.value.split(',').map(s => s.trim()).filter(Boolean);
                    updateSchedule({ email_recipients: emails });
                  }}
                  className="mt-1"
                />
                <p className="text-xs text-slate-400 mt-1">Comma-separated email addresses</p>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function ScheduleToggle({ label, description, checked, onChange }) {
  return (
    <div className="flex items-center justify-between p-3 rounded-lg bg-slate-50 dark:bg-slate-800/50">
      <div>
        <p className="text-sm font-medium">{label}</p>
        <p className="text-xs text-slate-400">{description}</p>
      </div>
      <button
        onClick={() => onChange(!checked)}
        className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${checked ? 'bg-indigo-600' : 'bg-slate-300'}`}
      >
        <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${checked ? 'translate-x-6' : 'translate-x-1'}`} />
      </button>
    </div>
  );
}

function TrafficChart({ data, xKey = 'date' }) {
  if (!data?.length) return (
    <div className="flex flex-col items-center justify-center h-full text-slate-400">
      <BarChart3 className="w-10 h-10 mb-2" />
      <p className="text-sm">No traffic data — connect GA4 to see sessions</p>
    </div>
  );
  return (
    <ResponsiveContainer width="100%" height="100%">
      <AreaChart data={data}>
        <defs>
          <linearGradient id="trafficGradientSub" x1="0" y1="0" x2="0" y2="1">
            <stop offset="5%" stopColor="#6366f1" stopOpacity={0.3} />
            <stop offset="95%" stopColor="#6366f1" stopOpacity={0} />
          </linearGradient>
        </defs>
        <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
        <XAxis dataKey={xKey} stroke="#94a3b8" fontSize={12} />
        <YAxis stroke="#94a3b8" fontSize={12} />
        <Tooltip contentStyle={{ backgroundColor: '#fff', border: '1px solid #e2e8f0', borderRadius: '8px' }} />
        <Area type="monotone" dataKey="sessions" stroke="#6366f1" fillOpacity={1} fill="url(#trafficGradientSub)" strokeWidth={2} name="Sessions" />
      </AreaChart>
    </ResponsiveContainer>
  );
}

function DailyReportSection({ report, loading, onRefresh }) {
  if (loading) return <div className="flex justify-center py-12"><Loader2 className="h-8 w-8 animate-spin text-indigo-500" /></div>;
  if (!report) return <Card><CardContent className="py-12 text-center text-slate-500"><Clock className="h-12 w-12 mx-auto mb-3 opacity-30" /><p>No daily report data available.</p></CardContent></Card>;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-semibold">Daily Report — {report.date}</h2>
        <Button variant="outline" size="sm" onClick={onRefresh}><Clock className="h-4 w-4 mr-1" /> Refresh</Button>
      </div>
      <div className="grid grid-cols-3 gap-4">
        <Card><CardContent className="pt-6 text-center">
          <div className="text-2xl font-bold">{report.keywords_tracked}</div>
          <div className="text-xs text-slate-500 mt-1">Keywords Tracked</div>
        </CardContent></Card>
        <Card><CardContent className="pt-6 text-center">
          <div className="text-2xl font-bold text-emerald-600">{report.new_content_published}</div>
          <div className="text-xs text-slate-500 mt-1">New Content</div>
        </CardContent></Card>
        <Card><CardContent className="pt-6 text-center">
          <div className="text-2xl font-bold text-indigo-600">{report.new_backlinks_acquired}</div>
          <div className="text-xs text-slate-500 mt-1">New Backlinks</div>
        </CardContent></Card>
      </div>
      <Card className="gds-card">
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <TrendingUp className="w-4 h-4 text-indigo-500" /> Sessions by Hour — Today
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="h-56">
            <TrafficChart data={report.chart_data} xKey="hour" />
          </div>
        </CardContent>
      </Card>
      {report.rank_changes?.length > 0 && (
        <Card>
          <CardHeader><CardTitle className="text-base flex items-center gap-2"><TrendingUp className="w-4 h-4 text-indigo-500" /> Rank Changes</CardTitle></CardHeader>
          <CardContent>
            <div className="space-y-2">
              {report.rank_changes.map((rc, i) => (
                <div key={i} className="flex items-center justify-between p-2 rounded bg-slate-50 dark:bg-slate-800/50">
                  <span className="text-sm font-medium">{rc.keyword}</span>
                  <div className="flex items-center gap-3">
                    <span className="text-sm text-slate-500">#{rc.position}</span>
                    <span className={`text-sm font-medium flex items-center gap-1 ${rc.change > 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                      {rc.change > 0 ? <ArrowUpRight className="w-3 h-3" /> : <ArrowDownRight className="w-3 h-3" />}
                      {rc.change > 0 ? '+' : ''}{rc.change}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

function WeeklyReportSection({ report, loading, onRefresh }) {
  if (loading) return <div className="flex justify-center py-12"><Loader2 className="h-8 w-8 animate-spin text-indigo-500" /></div>;
  if (!report) return <Card><CardContent className="py-12 text-center text-slate-500"><Calendar className="h-12 w-12 mx-auto mb-3 opacity-30" /><p>No weekly report data available.</p></CardContent></Card>;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-semibold">Weekly Report — Week ending {report.week_ending}</h2>
        <Button variant="outline" size="sm" onClick={onRefresh}><Calendar className="h-4 w-4 mr-1" /> Refresh</Button>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card><CardContent className="pt-6 text-center">
          <div className="text-2xl font-bold text-emerald-600">{report.keywords_improved}</div>
          <div className="text-xs text-slate-500 mt-1">Improved</div>
        </CardContent></Card>
        <Card><CardContent className="pt-6 text-center">
          <div className="text-2xl font-bold text-red-600">{report.keywords_declined}</div>
          <div className="text-xs text-slate-500 mt-1">Declined</div>
        </CardContent></Card>
        <Card><CardContent className="pt-6 text-center">
          <div className="text-2xl font-bold text-blue-600">{report.content_published}</div>
          <div className="text-xs text-slate-500 mt-1">Content Published</div>
        </CardContent></Card>
        <Card><CardContent className="pt-6 text-center">
          <div className="text-2xl font-bold text-indigo-600">{report.backlinks_acquired}</div>
          <div className="text-xs text-slate-500 mt-1">Backlinks Acquired</div>
        </CardContent></Card>
      </div>
      <Card className="gds-card">
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <TrendingUp className="w-4 h-4 text-indigo-500" /> Traffic — Last 7 Days
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="h-56">
            <TrafficChart data={report.chart_data} />
          </div>
        </CardContent>
      </Card>
      <Card>
        <CardContent className="pt-6">
          <div className="flex items-center gap-6 text-sm text-slate-600">
            <span><strong>{report.total_tracked}</strong> keywords tracked</span>
            <span><strong>{report.onpage_scans}</strong> on-page scans</span>
            <span><strong>{report.backlink_opportunities_found}</strong> new opportunities</span>
          </div>
        </CardContent>
      </Card>
      {report.keyword_summary?.length > 0 && (
        <Card>
          <CardHeader><CardTitle className="text-base">Keyword Movement Summary</CardTitle></CardHeader>
          <CardContent>
            <div className="space-y-2 max-h-80 overflow-y-auto">
              {report.keyword_summary.map((kw, i) => (
                <div key={i} className="flex items-center justify-between p-2 rounded bg-slate-50 dark:bg-slate-800/50">
                  <span className="text-sm font-medium">{kw.keyword}</span>
                  <div className="flex items-center gap-3">
                    <span className="text-sm text-slate-500">#{kw.position}</span>
                    <span className={`text-sm font-medium flex items-center gap-1 ${kw.change > 0 ? 'text-emerald-600' : kw.change < 0 ? 'text-red-600' : 'text-slate-400'}`}>
                      {kw.change > 0 ? <ArrowUpRight className="w-3 h-3" /> : kw.change < 0 ? <ArrowDownRight className="w-3 h-3" /> : null}
                      {kw.change > 0 ? '+' : ''}{kw.change}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

function ReportHistorySection({ history, loading }) {
  if (loading) return <div className="flex justify-center py-12"><Loader2 className="h-8 w-8 animate-spin text-indigo-500" /></div>;

  return (
    <div className="space-y-4">
      <h2 className="text-lg font-semibold">Report History</h2>
      {history.length === 0 ? (
        <Card><CardContent className="py-12 text-center text-slate-500">
          <History className="h-12 w-12 mx-auto mb-3 opacity-30" />
          <p>No reports generated yet. Reports will appear here as they are generated.</p>
        </CardContent></Card>
      ) : (
        <div className="space-y-2">
          {history.map((rpt) => (
            <Card key={rpt.report_id} className="hover:shadow-md transition-shadow">
              <CardContent className="py-3 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <Badge className={
                    rpt.report_type === 'daily' ? 'bg-blue-100 text-blue-700' :
                    rpt.report_type === 'weekly' ? 'bg-violet-100 text-violet-700' :
                    'bg-indigo-100 text-indigo-700'
                  }>
                    {rpt.report_type}
                  </Badge>
                  <span className="text-sm font-medium">{rpt.generated_at?.split('T')[0]}</span>
                </div>
                <span className="text-xs text-slate-400">{rpt.report_id}</span>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
